'use client'

// THEME COMPLIANCE: All UI implementations must support both dark and light themes.

import * as React from 'react'
import Image from 'next/image'
import { cn } from '@/lib/utils'

// Neumorphic shadow (neu tokens). As a drop-shadow FILTER it follows the
// parallelogram silhouette of the composed desktop asset; as a box-shadow it
// frames the straight mobile card.
const CARD_DROP_SHADOW =
  'drop-shadow(-10px -10px 30px var(--neu-shadow-light)) drop-shadow(10px 10px 30px var(--neu-shadow-dark))'
const CARD_BOX_SHADOW =
  '-10px -10px 30px var(--neu-shadow-light), 10px 10px 30px var(--neu-shadow-dark)'

// `card` = the exact Figma export (4202:181300) with the photo, rgba(13,13,13,0.78)
// overlay, blur and slanted parallelogram crop all baked in — used in the desktop
// overlapping band. `photo` = the raw photo, composited live for the clean mobile grid.
const FEATURES = [
  {
    id: 'leagues',
    title: 'MULTI-LEAGUES',
    description:
      'A bigger player pool means more strategy and more ways to build your ultimate squad.',
    card: '/assets/landing/features/card-multi-leagues.png',
    photo: '/assets/landing/features/multi-leagues.png',
    blurText: true
  },
  {
    id: 'free',
    title: 'FREE TO PLAY',
    description: 'Jump in with a squad to prove your management skills without spending a cent.',
    card: '/assets/landing/features/card-free-to-play.png',
    photo: '/assets/landing/features/free-to-play.png',
    blurText: true
  },
  {
    id: 'rewards',
    title: 'EPIC REWARDS',
    description:
      'Compete for a season prize pool of up to €200,000 across tiers and special events.',
    card: '/assets/landing/features/card-epic-rewards.png',
    photo: '/assets/landing/features/epic-rewards.png',
    bold: '€200,000'
  },
  {
    id: 'ai',
    title: 'AI MANAGER',
    description:
      'Smart recommendations for transfers, captaincy, fixtures, and minute-risk—your second pair of eyes.',
    card: '/assets/landing/features/card-ai-manager.png',
    photo: '/assets/landing/features/ai-manager.png'
  }
] as const

type Feature = (typeof FEATURES)[number]

// EPIC REWARDS bolds the "€200,000" figure inside the medium-weight copy.
function renderDescription(description: string, bold?: string) {
  if (!bold) return description
  const segments = description.split(bold)
  return segments.map((segment, index) => (
    <React.Fragment key={index}>
      {segment}
      {index < segments.length - 1 && <strong className="font-bold">{bold}</strong>}
    </React.Fragment>
  ))
}

function CardText({ feature, paddingClass }: { feature: Feature; paddingClass: string }) {
  return (
    <div className={cn('absolute inset-0 flex items-center justify-center', paddingClass)}>
      <div
        className={cn(
          'flex w-full flex-col items-center justify-center text-center',
          'gap-2 sm:gap-3 lg:gap-4 3xl:gap-[19px] rounded-[16px] px-2 py-2',
          'blurText' in feature && feature.blurText && 'backdrop-blur-[2px]'
        )}
      >
        <h3
          className={cn(
            'font-montserrat font-bold italic uppercase text-white',
            'text-sm sm:text-base md:text-lg xl:text-xl 2xl:text-[22px] 3xl:text-[24px]'
          )}
          style={{ lineHeight: 1.2, letterSpacing: '0.15px' }}
        >
          {feature.title}
        </h3>
        <p
          className={cn(
            'font-montserrat font-medium text-white',
            'text-[10px] sm:text-xs md:text-sm xl:text-[15px] 3xl:text-base'
          )}
          style={{ lineHeight: 1.25, letterSpacing: '0.15px' }}
        >
          {renderDescription(feature.description, 'bold' in feature ? feature.bold : undefined)}
        </p>
      </div>
    </div>
  )
}

export default function AdrenalineSection() {
  return (
    <section className="relative py-12 md:py-24 text-white overflow-hidden">
      <div className="px-4 sm:px-6 lg:px-8">
        {/* Section heading — "FEATURES" in Impact (Anton web fallback), like the hero.
            Text lives directly in <h2> so the global `* { font-family: Montserrat }`
            rule can't hijack a descendant. */}
        <h2
          className={cn(
            'text-center uppercase text-white mb-10 md:mb-16',
            'text-[36px] sm:text-5xl md:text-6xl lg:text-7xl xl:text-[80px] 3xl:text-[90px]'
          )}
          style={{
            fontFamily: '"Impact", var(--font-anton), "Arial Black", sans-serif',
            fontWeight: 400,
            lineHeight: 1.22,
            letterSpacing: '0.15px'
          }}
        >
          FEATURES
        </h2>

        {/* MOBILE — straight cards in a 2×2 grid (raw photo + live 0.78 overlay). */}
        <div className="mx-auto grid grid-cols-2 gap-4 sm:gap-5 md:gap-6 max-w-[560px] lg:hidden">
          {FEATURES.map((feature) => (
            <div
              key={feature.id}
              className="relative aspect-[517/603] w-full overflow-hidden rounded-[16px]"
              style={{ boxShadow: CARD_BOX_SHADOW }}
            >
              <Image
                src={feature.photo}
                alt={feature.title}
                fill
                sizes="50vw"
                className="object-cover scale-105 blur-[2px]"
              />
              <div
                className="absolute inset-0"
                style={{ backgroundColor: 'rgba(13,13,13,0.78)' }}
              />
              <CardText feature={feature} paddingClass="px-[8%]" />
            </div>
          ))}
        </div>

        {/* DESKTOP — overlapping band of the composed parallelogram assets (Figma 4202:181300).
            Each card is a straight 517×603 box; the slant/overlay/blur live in the PNG. */}
        <div className="hidden lg:flex lg:flex-row lg:items-center lg:justify-center">
          {FEATURES.map((feature) => (
            <div
              key={feature.id}
              className={cn(
                'relative aspect-[517/603] shrink-0',
                // Figma card size: 517×603 @ 3xl, scaling down per breakpoint
                'w-[260px] xl:w-[340px] 2xl:w-[420px] 3xl:w-[517px]',
                // Figma 83px overlap (scaled) so the four cards fill the row
                '-mr-[42px] xl:-mr-[55px] 2xl:-mr-[67px] 3xl:-mr-[83px] last:!mr-0'
              )}
            >
              {/* Composed card asset — bleeds ~2% beyond the box (Figma inset-[-1.99%_-2.32%]);
                  drop-shadow follows the parallelogram alpha. */}
              <div className="absolute inset-[-1.99%_-2.32%]" style={{ filter: CARD_DROP_SHADOW }}>
                <Image
                  src={feature.card}
                  alt={feature.title}
                  fill
                  sizes="517px"
                  className="object-fill"
                />
              </div>
              <CardText
                feature={feature}
                paddingClass="px-[22%] xl:px-[23%] 2xl:px-[24%] 3xl:px-[25%]"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
